<script setup>
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import { activationLinkState } from '../../../../helpers/activation';
import { hubSignInName } from '../../../../helpers/hubSignInName';
import { readPendingVerification } from '../../../../helpers/pendingVerification';
import { useAuthStore } from '../../../../stores/auth';
import Logo from '../../../preview/Logo.vue';
import ActivateAccount from './ActivateAccount.vue';
import AccountDetails from './AccountDetails.vue';
import CheckEmail from './CheckEmail.vue';
import ConfirmPassword from './ConfirmPassword.vue';
import CustomFields from './CustomFields.vue';
import DeadActivationLink from './DeadActivationLink.vue';
import ForgotPassword from './ForgotPassword.vue';
import SuccessMessage from './SuccessMessage.vue';
import UserAlreadyExists from './UserAlreadyExists.vue';
import VerifyCode from './VerifyCode.vue';

const userData = ref(false);
const params = new URLSearchParams(window.location.search);
const activationLink = params.get('activation_token') && params.get('email')
    ? {
        email: params.get('email'),
        activationToken: params.get('activation_token'),
        firstName: params.get('first_name') || '',
        lastName: params.get('last_name') || '',
    }
    : null;
const currentFormStep = ref(activationLink ? 'checking-link' : 'check-email');
const currentFormStepData = ref({});
const emailExist = ref(false);
const authStore = useAuthStore();
const { userAlreadyExistEmail } = storeToRefs(authStore);

defineProps({
	eventData: Object,
});

function changeFormStep(step, data = {}, doesEmailExist = false) {
	currentFormStep.value = step;
	currentFormStepData.value = data;
    emailExist.value = doesEmailExist;
};

function setUserData(data) {
	userData.value = data;
}

async function openActivationLink(link) {
    const state = await activationLinkState(link.email, link.activationToken);
    if (['used', 'expired', 'invalid'].includes(state)) {
        changeFormStep('dead-link', { email: link.email, state });
        return;
    }
    changeFormStep('activate-account', link);
}

onMounted(() => {
    if (activationLink) {
        openActivationLink(activationLink);
    } else if (userAlreadyExistEmail.value) {
        // Arrived already authenticated (e.g. Google) as an existing account that
        // isn't a member of this hub yet → collect custom fields (auto-joins if none).
        const ssoUser = authStore.pendingSsoUser || { email: userAlreadyExistEmail.value };
        if (!ssoUser.email) ssoUser.email = userAlreadyExistEmail.value;
        changeFormStep("custom-fields", { user: ssoUser }, true);
        authStore.setUserAlreadyExistEmail(null);
    } else {
        // A code was sent but never entered. The account exists and is
        // unverified, so starting from scratch would only offer a password
        // prompt it cannot satisfy — resume the code step, which can resend.
        const pendingEmail = readPendingVerification();
        if (pendingEmail) {
            changeFormStep('verify-code', { email: pendingEmail });
        }
    }

    document.title = `${hubSignInName()} REGISTRATION`.trim();
});
</script>

<template>
	<div class="register">
		<!-- The hub's name, not a way out: it used to link to "/", which on
		     the sign-in page is the page itself. Plain, as on password reset. -->
		<div class="register__image">
			<Logo :logo="eventData.settings.logo" />
		</div>
		<div class="register__form">
			<ActivateAccount :event-data="eventData" :change-form-step="changeFormStep"
				v-if="currentFormStep === 'activate-account'" :current-form-step-data="currentFormStepData" />
			<CheckEmail :event-data="eventData" :change-form-step="changeFormStep" :user-data="userData"
				:set-user-data="setUserData" v-if="currentFormStep === 'check-email'"
				:current-form-step-data="currentFormStepData" />
			<ConfirmPassword :event-data="eventData" :change-form-step="changeFormStep"
				v-if="currentFormStep === 'confirm-password'" :current-form-step-data="currentFormStepData" />
			<ForgotPassword :event-data="eventData" :change-form-step="changeFormStep"
				v-if="currentFormStep === 'forgot-password'" :current-form-step-data="currentFormStepData" />
			<AccountDetails :event-data="eventData" :change-form-step="changeFormStep"
				v-if="currentFormStep === 'account-details'" :current-form-step-data="currentFormStepData" />
			<CustomFields :event-data="eventData" :change-form-step="changeFormStep"
				v-if="currentFormStep === 'custom-fields'" :current-form-step-data="currentFormStepData"
				:email-exist="emailExist" />
			<VerifyCode :event-data="eventData" :change-form-step="changeFormStep"
				v-if="currentFormStep === 'verify-code'" :current-form-step-data="currentFormStepData" />
			<DeadActivationLink v-if="currentFormStep === 'dead-link'"
				:current-form-step-data="currentFormStepData" />
			<UserAlreadyExists v-if="currentFormStep === 'user-already-exist'"
				:current-form-step-data="currentFormStepData" />
			<SuccessMessage :event-data="eventData" v-if="currentFormStep === 'success-message'"
				:current-form-step-data="currentFormStepData" :email-exist="emailExist" />
		</div>
	</div>
</template>

<style lang="scss" scoped>
.register {
	display: flex;
	flex-direction: column;
	gap: 22px;

	&__title {
		text-align: center;
		font-weight: 600;
		font-size: 24px;
		line-height: 1.5;
	}

	&__form {
		display: flex;
		flex-direction: column;
		gap: 15px;

		.d-align-center {
			display: flex;
			gap: 15px;
			flex-direction: column;
			align-items: center;
		}

		.link-blue {
			color: #3d66be;
			text-decoration: none;
			font-size: 14px;
		}

		.link-purple {
			color: #583174;
			text-decoration: none;
		}

		&__actions {
			display: flex;
			justify-content: space-between;
			align-items: center;
		}

		&__errors {
			display: flex;
			flex-direction: column;
			gap: 10px;

			div {
				text-align: center;
				color: #f83c3c;
				font-weight: 700;
				font-size: 14px;
				line-height: 20px;
			}
		}
	}
}
</style>
